import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, FileUp, Loader2, Upload } from 'lucide-react';
import { isUploadFileAllowed, uploadAccept, uploadAcceptLabel } from '@peles/shared';
import { AcceptedFileTypesNote } from '../components/common/AcceptedFileTypesNote';
import type { VendorUploadInfo } from '@peles/shared';
import {
  createVendorUpload,
  getVendorUploadInfo,
  getVendorUploadUrl,
} from '../api/publicVendorUpload';

const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

type Phase = 'loading' | 'ready' | 'not-found' | 'uploading' | 'done' | 'used';

/**
 * Public, tokenized page where a vendor uploads a file for an event. No login —
 * the token in the URL is the capability. The upload is attached to the vendor
 * on the event and both the producer and vendor are emailed.
 */
export function PublicVendorUploadPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<Phase>('loading');
  const [info, setInfo] = useState<VendorUploadInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadedName, setUploadedName] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let active = true;
    getVendorUploadInfo(token)
      .then((data) => {
        if (!active) return;
        setInfo(data);
        // Single-use link: a consumed token can't upload again.
        setPhase(data.used ? 'used' : 'ready');
      })
      .catch(() => {
        if (!active) return;
        setPhase('not-found');
      });
    return () => {
      active = false;
    };
  }, [token]);

  async function handleUpload(file: File) {
    setError(null);
    /*
     * ⚠️ **Checked here as well as on the server, and neither is redundant.**
     * The `accept` attribute below only filters a file dialog — a file can be
     * dropped past it — so without this a guest could pick a wrong file, wait
     * out the upload, and be refused at the end. The server still refuses too,
     * because this check runs on the guest's machine and is not a control.
     */
    if (!isUploadFileAllowed(info?.allowedFileTypes ?? [], file.type, file.name)) {
      setError(
        `This link accepts ${uploadAcceptLabel(info?.allowedFileTypes ?? []) ?? 'any file'}.`,
      );
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setError('File is too large (max 25 MB).');
      return;
    }
    setPhase('uploading');
    try {
      const mimeType = file.type || 'application/octet-stream';
      const { uploadUrl, s3Key } = await getVendorUploadUrl(token, file.name, mimeType);
      const putResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': mimeType },
        body: file,
      });
      if (!putResponse.ok) throw new Error('Upload failed');
      await createVendorUpload(token, {
        s3Key,
        fileName: file.name,
        mimeType,
        fileSizeBytes: file.size,
      });
      setUploadedName(file.name);
      setPhase('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
      setPhase('ready');
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8">
        {phase === 'loading' && (
          <div className="flex items-center justify-center py-10 text-gray-400">
            <Loader2 className="animate-spin" size={28} />
          </div>
        )}

        {phase === 'not-found' && (
          <div className="text-center py-6">
            <h1 className="text-lg font-semibold text-gray-900">Upload link not found</h1>
            <p className="mt-2 text-sm text-gray-500">
              This upload link is invalid or has expired. Please ask for a new one.
            </p>
          </div>
        )}

        {(phase === 'ready' || phase === 'uploading') && info && (
          <div className="text-center">
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <FileUp className="text-primary-600" size={22} />
            </div>
            <h1 className="text-lg font-semibold text-gray-900">Upload a file</h1>
            <p className="mt-1 text-sm text-gray-600">
              {info.vendorName} — {info.eventName}
            </p>
            {info.label && <p className="mt-1 text-xs text-gray-400">{info.label}</p>}
            <AcceptedFileTypesNote fileTypes={info.allowedFileTypes} />

            {error && (
              <div className="mt-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={phase === 'uploading'}
              className="mt-5 w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60"
              data-testid="vendor-upload-button"
            >
              {phase === 'uploading' ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Uploading…
                </>
              ) : (
                <>
                  <Upload size={18} />
                  Choose a file
                </>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={uploadAccept(info.allowedFileTypes) || undefined}
              className="hidden"
              data-testid="vendor-upload-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleUpload(file);
                e.target.value = '';
              }}
            />
            <p className="mt-3 text-xs text-gray-400">Max 25 MB. No login needed.</p>
          </div>
        )}

        {phase === 'done' && (
          <div className="text-center py-4" data-testid="vendor-upload-success">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="text-green-600" size={24} />
            </div>
            <h1 className="text-lg font-semibold text-gray-900">File received</h1>
            <p className="mt-2 text-sm text-gray-600">
              Thanks — we received <span className="font-medium">{uploadedName}</span>. A
              confirmation has been emailed to you.
            </p>
            <p className="mt-3 text-xs text-gray-400">
              Need to send another file? Ask us for a new upload link.
            </p>
          </div>
        )}

        {phase === 'used' && (
          <div className="text-center py-6" data-testid="vendor-upload-used">
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="text-gray-500" size={24} />
            </div>
            <h1 className="text-lg font-semibold text-gray-900">Already uploaded</h1>
            <p className="mt-2 text-sm text-gray-600">
              This upload link has already been used. If you need to send another file, please ask
              us for a new link.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
